<template>
  <span data-toggle="tooltip" data-placement="left" data-html="true" :title="tooltipContent">
    <slot></slot>
  </span>
</template>

<script>
import { formatCurrency, formatPeriod } from "../../../utils/helpers";
import '../../../../sass/commissions/transactions-tooltip.scss';

export default {
  name: 'CommissionTransactionsTooltip',
  props: {
    transactions: {
      type: Array,
      default: () => []
    }
  },
  mounted() {
    $(this.$el).tooltip({
      html: true,
      template: `
        <div class="tooltip commission-transactions" role="tooltip">
          <div class="arrow"></div>
          <div class="tooltip-inner"></div>
        </div>
      `,
      delay: {"show": 200, "hide": 100}
    });
  },
  beforeDestroy() {
    $(this.$el).tooltip('dispose');
  },
  computed: {
    tooltipContent() {
      if (!this.transactions || this.transactions.length === 0) {
        return 'No transactions recorded';
      }

      const sortedTransactions = this.sortTransactions(this.transactions);
      const paidCommissionTotal = this.calculateTotalAmount('paid', ['commission', 'change', 'cancellation', 'qty_enhancer', 'level_enhancer'], sortedTransactions);

      const paidCommissionRows = this.generateTransactionRows('paid', 'commission', sortedTransactions);
      const toBePaidCommissionRows = this.generateTransactionRows('to_be_paid', 'commission', sortedTransactions);
      const toBePaidPendingRows = this.generateTransactionRows('to_be_paid', 'pending', sortedTransactions);
      const changePaidCommissionRows = this.generateTransactionRows('paid', 'change', sortedTransactions);
      const changePendingCommissionRows = this.generateTransactionRows('pending', 'change', sortedTransactions);
      const pendingRemainingRows = this.generateTransactionRows('remaining', 'pending', sortedTransactions);
      const pendingCancellationRows = this.generateTransactionRows('pending', 'cancellation', sortedTransactions);
      const paidCancellationRows = this.generateTransactionRows('paid', 'cancellation', sortedTransactions);
      const paidQtyEnhancerRows = this.generateTransactionRows('paid', 'qty_enhancer', sortedTransactions);
      const paidLevelEnhancerRows = this.generateTransactionRows('paid', 'level_enhancer', sortedTransactions);
      const paidTransactionRows = this.generateTransactionRows('paid', 'transaction', sortedTransactions);
      const pendingTransactionRows = this.generateTransactionRows('pending', 'transaction', sortedTransactions);

      const totalPaidCommissionRow = (paidCommissionRows.length || changePaidCommissionRows.length) ? `
        <div class="row row-body total-row">
          <div class="col-md-4">Total Paid Commission</div>
          <div class="col-md-2">${formatCurrency(paidCommissionTotal)}</div>
          <div class="col-md-6"></div>
        </div>
      ` : '';

      return `
      <div class="commission-tooltip-content total">
        <div class="row row-header">
          <div class="col-md-2">Month</div>
          <div class="col-md-2">Qty Enh.</div>
          <div class="col-md-2">Payment</div>
          <div class="col-md-2">Status</div>
          <div class="col-md-4">Description</div>
        </div>
        ${paidCommissionRows}
        ${paidTransactionRows}
        ${changePaidCommissionRows}
        ${paidQtyEnhancerRows}
        ${paidLevelEnhancerRows}
        ${paidCancellationRows}
        ${totalPaidCommissionRow}
        ${toBePaidCommissionRows}
        ${toBePaidPendingRows}
        ${pendingTransactionRows}
        ${changePendingCommissionRows}
        ${pendingRemainingRows}
        ${pendingCancellationRows}
      </div>
    `;
    }
  },
  methods: {
    sortTransactions(transactions) {
      const priority = {
        'paid-commission': 1,
        'paid-transaction': 2,
        'paid-change': 3,
        'paid-cancellation': 4,
        'to_be_paid-commission': 5,
        'to_be_paid-pending': 6,
        'pending-transaction': 7,
        'pending-change': 8,
        'remaining-pending': 9,
        'pending-cancellation': 10,
      };

      return transactions.slice().sort((a, b) => {
        const aKey = `${a.status}-${a.type}`;
        const bKey = `${b.status}-${b.type}`;

        return (priority[aKey] || Infinity) - (priority[bKey] || Infinity);
      });
    },
    calculateTotalAmount(status, types, transactions) {
      return transactions
        .filter(transaction => transaction.status === status && types.includes(transaction.type))
        .reduce((total, transaction) => total + parseFloat(transaction.amount), 0);
    },
    generateTransactionRows(status, type, transactions) {
      return transactions
        .filter(transaction => transaction.status === status && transaction.type === type)
        .map(transaction => this.createTransactionRow(transaction))
        .join('');
    },
    createTransactionRow(transaction) {
      const statusClass = this.getStatusClass(transaction.status, transaction.type);
      const status = this.getStatusText(transaction.status, transaction.type, transaction.to_be_paid);
      const qtyKey = this.getQtyKey(transaction.type, transaction.status);
      const period = formatPeriod(transaction.processed_at, null, true);
      const isPending = transaction.status === 'pending' || transaction.status === 'remaining';
      const description = transaction.description === null ? '' : transaction.description;

      return `
        <div class="row row-body ${statusClass}">
          <div class="col-md-2">${isPending ? 'Pending' : period}</div>
          <div class="col-md-2">${this.getQtyPercentage(transaction.data?.[qtyKey] ?? transaction[qtyKey])}</div>
          <div class="col-md-2">${formatCurrency(transaction.amount)}</div>
          <div class="col-md-2">${status}</div>
          <div class="col-md-4">${description}</div>
        </div>
      `;
    },
    getQtyKey(type, status) {
      if (type === 'qty_enhancer' && status === 'paid') {
        return 'percentage';
      }
      if (type === 'pending' && (status === 'to_be_paid' || status === 'remaining')) {
        return 'qty_remaining';
      }

      return 'qty';
    },
    getQtyPercentage(student_qty) {
      return student_qty ? `${student_qty}%` : '-';
    },
    getStatusClass(status, type) {
      const rowClass = {
        commission: {
          paid: 'bg-paid',
          to_be_paid: 'bg-to-be-paid',
        },
        transaction: {
          paid: 'bg-paid',
          pending: 'bg-pending',
        },
        change: {
          paid: 'bg-change-paid',
          pending: 'bg-change-pending',
        },
        pending: {
          to_be_paid: 'bg-to-be-paid',
          remaining: 'bg-pending',
        },
        cancellation: {
          paid: 'bg-cancellation',
          pending: 'bg-cancellation',
        },
        qty_enhancer: {
          paid: 'bg-paid'
        },
        level_enhancer: {
          paid: 'bg-paid'
        },
      };

      return rowClass[type]?.[status] || '';
    },
    getStatusText(status, type, toBePaidAt) {
      if (status === 'to_be_paid' && (type === 'commission' || type === 'pending') ) {
        return `To be paid in ${formatPeriod(toBePaidAt, null, true)}`;
      } else if (status === 'paid' && (type === 'commission' || type === 'qty_enhancer' || type === 'level_enhancer')) {
        return status.charAt(0).toUpperCase() + status.slice(1);
      } else if (type === 'change') {
        return 'Change';
      } else if (type === 'cancellation') {
        return 'Cancellation';
      }

      return '';
    }
  }
}
</script>